import { Component, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { take } from 'rxjs/operators';

/** Services */
import { I18nService } from 'src/app/core/services/i18n.service';
import { ToggleService } from 'src/app/core/services/toggle.service';
import { CustomizerSettingsService } from 'src/app/core/services/customizer-settings.service';
import { EnumsService } from '../shared/services/enums.service';
import { EnumsDataService } from '../shared/services/enums-data.service';

/** Components */
import { HeaderComponent } from 'src/app/shell/header/header.component';
import { FooterComponent } from 'src/app/shell/footer/footer.component';
import { CustomizerSettingsComponent } from 'src/app/shell/customizer-settings/customizer-settings.component';
import { SidebarComponent } from 'src/app/shell/sidebar/sidebar.component';
import { BreadcrumbComponent } from 'src/app/shell/breadcrumb/breadcrumb.component';

import { LoadingOverlayComponent } from '../shared/components/loading-overlay/loading-overlay.component';
import { AppComponent } from '../app.component';
import { ChecklistTemplatesComponent } from '../components/checklist/checklist-templates/checklist-templates.component';
import { SubcityDataService } from '../shared/services/subcity-data.service';
import { SettingsService } from '../core/services/settings.service';
import { WoredaDataService } from '../shared/services/woreda-data.service';
import { ProfileService } from '../components/profile/profile.service';
import { AuthService } from '../core/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../core/services/alert.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    SidebarComponent,
    CustomizerSettingsComponent,
    BreadcrumbComponent,
    LoadingOverlayComponent,
  ],
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
})
export class ShellComponent {
  title = 'web-app';
  isToggled = false;
  profile: any;

  constructor(
    public snackBar: MatSnackBar,
    public router: Router,
    private toggleService: ToggleService,
    public themeService: CustomizerSettingsService,
    private enumsService: EnumsService,
    private settingsService: SettingsService,
    private enumsDataService: EnumsDataService,
    private subcityDataService: SubcityDataService,
    private woredaDataService: WoredaDataService,
    private profileService: ProfileService,
    private authService: AuthService,
    private translateService: TranslateService,
    private alertService: AlertService
  ) {
    this.toggleService.isToggled$.subscribe((isToggled) => {
      this.isToggled = isToggled;
    });
  }
  ngAfterViewInit(): void {
    this.calcHeight();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.calcHeight();
  }

  calcHeight() {
    const appShell = document.getElementById('app-admin-shell');
    const header = document.getElementById('dashboard-header');
    const routerOutlet = document.getElementById('router-outlet');

    if (appShell && header && routerOutlet) {
      const height = appShell.clientHeight - header.clientHeight;
      routerOutlet.style.height = `${height}px`;
    }
  }

  ngOnInit(): void {
    this.enumsService.getAllEnums().subscribe((enums) => {
      this.enumsDataService.setEnumsData(enums);
    });
    this.settingsService.getSubCities().subscribe((subcities) => {
      this.subcityDataService.setSubcityData(subcities);
    });
    this.settingsService.getWoredas().subscribe((woredas) => {
      this.woredaDataService.setWoredaData(woredas);
    });

    // Add timestamp to see when emissions occur
    this.authService.profile$.subscribe((userProfile) => {
      if (userProfile) {
        this.profile = userProfile;
        if (
          this.profile &&
          (this.profile.attributes?.userType[0] == 'Institution' ||
            this.profile.attributes?.userType[0] == '2')
        ) {
          this.profileService
            .getInstitution(this.profile?.attributes?.institutionId[0])
            .subscribe({
              next: (institution) => {
                const isProfileIncomplete =
                  !institution.name ||
                  !institution.tin ||
                  !institution?.headOfficeAddress ||
                  !institution?.headOfficeAddress?.city ||
                  !institution?.headOfficeAddress?.subCity ||
                  !institution?.headOfficeAddress?.woreda ||
                  !institution.representative?.firstName ||
                  !institution.representative?.middleName ||
                  !institution.representative?.lastName ||
                  !institution?.representative?.address.city ||
                  !institution?.representative.address ||
                  !institution?.representative?.address.subCity ||
                  !institution?.representative?.address.woreda ||
                  !institution.representative?.userInfo?.email;

                if (isProfileIncomplete) {
                  this.router.navigate([
                    '/profile/institution/' +
                      this.profile.attributes.institutionId[0] +
                      '/edit',
                  ]);
                }
              },
              error: (err) => {
                console.error('Error fetching institution details:', err);
              },
            });
        } else if (
          this.profile &&
          (this.profile.attributes?.userType[0] == 'Individual' ||
            this.profile.attributes?.userType[0] == '1')
        ) {
          this.profileService
            .getPerson(this.profile.attributes.personId[0])
            .subscribe({
              next: (person) => {
                // Handle person data if needed
                const isProfileIncomplete =
                  !person.firstName ||
                  !person.middleName ||
                  !person.lastName ||
                  !person.userInfo?.phoneNumber ||
                  !person.userInfo.email
                  // !person.address?.city ||
                  // !person.address?.subCity ||
                  // !person.address?.woreda;

                if (isProfileIncomplete) {
                  this.alertService.alert({
                    type: 'Success',
                    message: this.translateService.instant(
                      'please_complete_your_profile_to_continue.capitalize'
                    ),
                  });
                  this.router.navigate([
                    '/profile/individual/' +
                      this.profile.attributes.personId[0] +
                      '/edit',
                  ]);
                }
              },
              error: (err) => {
                console.error('Error fetching person details:', err);
              },
            });
        } else if (
          this.profile &&
          (this.profile.attributes?.userType[0] == 'Assessor' ||
            this.profile.attributes?.userType[0] == '4')
        ) {
          this.profileService
            .getPerson(this.profile.attributes.personId[0])
            .subscribe({
              next: (person) => {
                // Handle person data if needed
                const isProfileIncomplete =
                  !person.firstName ||
                  !person.middleName ||
                  !person.lastName ||
                  !person.userInfo?.phoneNumber ||
                  !person.userInfo.email ||
                  !person.address?.city ||
                  !person.address?.subCity ||
                  !person.address?.woreda;

                if (isProfileIncomplete) {
                  this.alertService.alert({
                    type: 'Success',
                    message: this.translateService.instant(
                      'please_complete_your_profile_to_continue.capitalize'
                    ),
                  });
                  this.router.navigate([
                    '/profile/assessor/' +
                      this.profile.attributes.personId[0] +
                      '/edit',
                  ]);
                }
              },
              error: (err) => {
                console.error('Error fetching person details:', err);
              },
            });
        }
      }
    });
  }

  toggleRightSidebarTheme() {
    this.themeService.toggleRightSidebarTheme();
  }

  toggleHideSidebarTheme() {
    this.themeService.toggleHideSidebarTheme();
  }

  toggleCardBorderTheme() {
    this.themeService.toggleCardBorderTheme();
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  toggleCardBorderRadiusTheme() {
    this.themeService.toggleCardBorderRadiusTheme();
  }

  toggleRTLEnabledTheme() {
    this.themeService.toggleRTLEnabledTheme();
  }
}
